import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Gift, Package, RefreshCw, Settings2, SlidersHorizontal, X } from "lucide-react";
import toast from "react-hot-toast";

import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { SpinnerBlock } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import {
  bulkReviewTikTokSamples,
  getTikTokSamplePolicy,
  listTikTokSamples,
  reviewTikTokSample,
  syncTikTokSamples,
  type TikTokCreatorPick,
  type TikTokRejectReason,
  type TikTokSampleApplication,
  type TikTokSamplePolicy,
  type TikTokSampleTab,
} from "@/services/tiktokShop.service";
import { formatCompactCount } from "@/utils/formatters";

import { TikTokBlockedState } from "../TikTokBlockedState";
import {
  blockFromError,
  CANCELLED_LABEL,
  errorMessage,
  orPrivate,
  REJECT_REASON_LABEL,
  SAMPLE_STAGE,
  timeAgo,
  timeLeft,
  usd,
} from "../tiktokFormat";
import { SampleDetailSheet } from "./SampleDetailSheet";
import { SamplePolicySheet } from "./SamplePolicySheet";
import { SuggestionChip } from "./SuggestionChip";
import { SampleRulesSheet } from "./SampleRulesSheet";

const TABS: Array<{ value: TikTokSampleTab; label: string }> = [
  { value: "TO_REVIEW", label: "To review" },
  { value: "IN_PROGRESS", label: "In progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

const pick = (s: TikTokSampleApplication): TikTokCreatorPick => ({
  openId: s.creator.openId,
  username: s.creator.username,
  nickname: s.creator.nickname,
  avatarUrl: s.creator.avatarUrl,
  followerCount: s.creator.followerCount,
});

const canReview = (s: TikTokSampleApplication) => s.status === "PENDING" && !s.partnerName;

function CreatorStats({ s }: { s: TikTokSampleApplication }) {
  const c = s.creator;
  return (
    <span className="text-xs tabular-nums text-muted-foreground">
      {orPrivate(c.followerCount, formatCompactCount)} followers · {orPrivate(c.gmv, usd)} GMV
      <br className="hidden lg:block" />
      <span className="lg:hidden"> · </span>
      {orPrivate(c.fulfillmentRate, (n) => `${Math.round(n)}%`)} sample fulfillment · {orPrivate(c.medianViews, formatCompactCount)} views
    </span>
  );
}

function RejectMenu({
  disabled,
  onReject,
  label = "Reject",
  size = "sm",
}: {
  disabled?: boolean;
  onReject: (reason: TikTokRejectReason) => void;
  label?: string;
  size?: "sm" | "default";
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size={size} disabled={disabled} onClick={(e) => e.stopPropagation()}>
          <X className="h-3.5 w-3.5" />
          {label}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">The creator sees this reason</DropdownMenuLabel>
        {(Object.keys(REJECT_REASON_LABEL) as TikTokRejectReason[]).map((reason) => (
          <DropdownMenuItem key={reason} onSelect={() => onReject(reason)}>
            {REJECT_REASON_LABEL[reason]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function StageCell({ s }: { s: TikTokSampleApplication }) {
  if (s.status === "PENDING") {
    if (s.partnerName) return <span className="text-xs text-muted-foreground">Agency reviews</span>;
    const left = timeLeft(s.approveExpiresAt);
    return left ? (
      <span className={cn("text-xs tabular-nums", left.urgent ? "font-medium text-red-600 dark:text-red-400" : "text-muted-foreground")}>
        Review within {left.label}
      </span>
    ) : (
      <span className="text-xs text-muted-foreground">Review window closing</span>
    );
  }
  const stage = SAMPLE_STAGE[s.status];
  if (stage) {
    return (
      <span className="flex flex-col gap-1">
        <span className="flex gap-0.5" aria-hidden>
          {[1, 2, 3, 4].map((i) => (
            <span key={i} className={cn("h-1 w-5 rounded-full", i <= stage.step ? "bg-brand-500" : "bg-muted")} />
          ))}
        </span>
        <span className="text-xs text-muted-foreground">
          {stage.label}
          {s.status.includes("COMPLETED") && s.contentCount > 0 ? ` · ${s.contentCount} post${s.contentCount === 1 ? "" : "s"}` : ""}
        </span>
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">{CANCELLED_LABEL[s.status] ?? s.status}</span>;
}

function SampleRow({
  s,
  selected,
  onSelect,
  onOpen,
  onApprove,
  onReject,
  busy,
}: {
  s: TikTokSampleApplication;
  selected: boolean;
  onSelect: (checked: boolean) => void;
  onOpen: () => void;
  onApprove: () => void;
  onReject: (reason: TikTokRejectReason) => void;
  busy: boolean;
}) {
  const reviewable = canReview(s);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => e.key === "Enter" && onOpen()}
      className="grid cursor-pointer grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2 border-b border-border px-4 py-3 last:border-b-0 hover:bg-muted/40 lg:grid-cols-[auto_minmax(0,1.3fr)_minmax(0,1.2fr)_minmax(0,1fr)_auto]"
    >
      <span className="flex items-center" onClick={(e) => e.stopPropagation()}>
        {s.status === "PENDING" ? (
          <Checkbox checked={selected} disabled={!reviewable} onCheckedChange={(v) => onSelect(v === true)} aria-label={`Select @${s.creator.username}`} />
        ) : (
          <span className="w-4" />
        )}
      </span>

      <span className="flex min-w-0 items-center gap-2.5">
        <UserAvatar src={s.creator.avatarUrl} name={s.creator.nickname || s.creator.username} className="h-9 w-9 shrink-0 text-xs" />
        <span className="flex min-w-0 flex-col">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-sm font-medium text-foreground">{s.creator.nickname || `@${s.creator.username}`}</span>
            {s.affiliate && (
              <span className="shrink-0 rounded-full bg-brand-100 px-1.5 py-px text-[10px] font-medium text-brand-700 dark:bg-brand-950 dark:text-brand-300" title={s.affiliate.name}>
                Your affiliate
              </span>
            )}
          </span>
          <CreatorStats s={s} />
        </span>
      </span>

      <span className="col-start-2 flex min-w-0 items-center gap-2.5 lg:col-start-auto">
        {s.product.imageUrl ? (
          <img src={s.product.imageUrl} alt="" className="h-9 w-9 shrink-0 rounded-md object-cover" />
        ) : (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted">
            <Package className="h-4 w-4 text-muted-foreground" />
          </span>
        )}
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-sm text-foreground">{s.product.title ?? s.product.id}</span>
          <span className="truncate text-xs text-muted-foreground">
            {s.product.skuName ?? "Default"}
            {s.commissionRate ? ` · ${Math.round(Number(s.commissionRate) * 100)}% commission` : ""}
          </span>
        </span>
      </span>

      <span className="col-start-2 flex min-w-0 flex-col gap-1 lg:col-start-auto">
        <StageCell s={s} />
        {s.status === "PENDING" ? (
          <SuggestionChip suggestion={s.suggestion} />
        ) : (
          s.reviewedBy === "AUTO" && (
            <span className="w-fit rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
              {s.rejectReason ? "Auto-rejected" : "Auto-approved"}
            </span>
          )
        )}
        {!s.isApprovable && s.status === "PENDING" && !s.suggestion && s.disapprovableReasons.length > 0 && (
          <span className="text-[11px] text-red-600 dark:text-red-400">{s.disapprovableReasons.join(", ")}</span>
        )}
      </span>

      <span className="col-start-2 flex items-center gap-2 lg:col-start-auto lg:justify-end" onClick={(e) => e.stopPropagation()}>
        {reviewable && (
          <>
            <Button type="button" size="sm" disabled={!s.isApprovable || busy} onClick={onApprove}>
              <Check className="h-3.5 w-3.5" />
              Approve
            </Button>
            <RejectMenu disabled={busy} onReject={onReject} />
          </>
        )}
      </span>
    </div>
  );
}

function policySummary(policy: TikTokSamplePolicy | undefined): { title: string; body: string } {
  if (!policy || policy.mode === "OFF") {
    return { title: "Manual review", body: "Every request waits for you. Set review rules to get suggestions or let Reveshare decide." };
  }
  const rules = [
    policy.minFollowers !== null && `${formatCompactCount(policy.minFollowers)}+ followers`,
    policy.minGmv !== null && `${usd(policy.minGmv)}+ GMV`,
    policy.minFulfillmentRate !== null && `${policy.minFulfillmentRate}%+ fulfillment`,
    policy.minMedianViews !== null && `${formatCompactCount(policy.minMedianViews)}+ views`,
  ].filter(Boolean);
  const criteria = rules.length ? rules.join(", ") : "no thresholds yet";
  if (policy.mode === "SUGGEST") return { title: "Suggestions on", body: `Each request is checked against ${criteria}. You still decide.` };
  return {
    title: "Auto-review on",
    body: `Approves ${criteria}${policy.approveAffiliates ? ", and your affiliates" : ""}${
      policy.rejectBelow ? "; rejects the rest" : "; leaves the rest for you"
    }${policy.dailyApprovalCap ? `; up to ${policy.dailyApprovalCap} a day` : ""}.`,
  };
}

/**
 * Creators' free-sample requests on the brand's TikTok Shop. TikTok runs the
 * lifecycle; this tab is where the brand decides, sets the rules that decide
 * for it, and sees what each sample turned into.
 */
export function SamplesTab({
  onMessage,
  onInvite,
}: {
  onMessage: (creator: TikTokCreatorPick) => void;
  onInvite: (creators: TikTokCreatorPick[]) => void;
}) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TikTokSampleTab>("TO_REVIEW");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  const list = useQuery({
    queryKey: ["tiktok-samples", tab],
    queryFn: () => listTikTokSamples(tab),
    refetchInterval: 60_000,
  });
  const policy = useQuery({ queryKey: ["tiktok-sample-policy"], queryFn: getTikTokSamplePolicy, staleTime: 60_000 });

  const sync = useMutation({
    mutationFn: syncTikTokSamples,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["tiktok-samples"] });
      const auto = result.autoApproved + result.autoRejected;
      if (auto > 0) {
        toast.success(
          `Auto-review handled ${auto} request${auto === 1 ? "" : "s"}: ${result.autoApproved} approved, ${result.autoRejected} rejected`,
        );
      }
    },
  });
  const { mutate: runSync } = sync;
  useEffect(() => {
    runSync();
  }, [runSync]);

  useEffect(() => setSelected(new Set()), [tab]);

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ["tiktok-samples"] });
  };

  const markBusy = (ids: string[], on: boolean) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });

  const review = async (s: TikTokSampleApplication, result: "APPROVE" | "REJECT", reason?: TikTokRejectReason) => {
    markBusy([s.id], true);
    try {
      await reviewTikTokSample(s.id, result, reason);
      toast.success(result === "APPROVE" ? `Approved @${s.creator.username}’s sample` : `Rejected @${s.creator.username}’s request`);
      refreshAll();
    } catch (error) {
      toast.error(errorMessage(error));
      refreshAll();
    } finally {
      markBusy([s.id], false);
    }
  };

  const bulk = async (result: "APPROVE" | "REJECT", reason?: TikTokRejectReason) => {
    const ids = [...selected];
    markBusy(ids, true);
    try {
      const results = await bulkReviewTikTokSamples(ids, result, reason);
      const ok = results.filter((r) => r.ok).length;
      const failed = results.filter((r) => !r.ok);
      if (ok) toast.success(`${result === "APPROVE" ? "Approved" : "Rejected"} ${ok} request${ok === 1 ? "" : "s"}`);
      if (failed.length) toast.error(`${failed.length} could not be ${result === "APPROVE" ? "approved" : "rejected"}: ${failed[0].message ?? ""}`);
      setSelected(new Set());
      refreshAll();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      markBusy(ids, false);
    }
  };

  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const selectable = useMemo(() => items.filter((s) => canReview(s) && s.isApprovable), [items]);
  const allSelected = selectable.length > 0 && selectable.every((s) => selected.has(s.id));
  const block = sync.error ? blockFromError(sync.error) : null;
  const totalRows = list.data ? Object.values(list.data.counts).reduce((a, b) => a + b, 0) : 0;
  const summary = policySummary(policy.data);

  if (block && block.kind !== "error" && totalRows === 0) {
    return <TikTokBlockedState block={block} feature="sample requests" onRetry={() => runSync()} />;
  }

  return (
    <div className="space-y-4">
      {block && block.kind !== "error" && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          Showing the last synced requests. {block.kind === "awaiting_approval" ? "TikTok has not approved the sample permissions yet." : "Reconnect your TikTok Shop to sync again."}
        </div>
      )}

      <div className="flex flex-col gap-3 rounded-lg border border-border bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-100 text-brand-700 dark:bg-brand-950 dark:text-brand-300">
            <SlidersHorizontal className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">{summary.title}</p>
            <p className="text-xs text-muted-foreground">{summary.body}</p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setPolicyOpen(true)}>
            <Settings2 className="h-3.5 w-3.5" />
            Review rules
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setRulesOpen(true)}>
            <Gift className="h-3.5 w-3.5" />
            Who can request
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div role="tablist" aria-label="Sample status" className="inline-flex flex-wrap rounded-lg border border-border bg-muted p-0.5">
          {TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={tab === t.value}
              onClick={() => setTab(t.value)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                tab === t.value ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t.label}
              <span className="rounded-full bg-background px-1.5 text-[11px] tabular-nums text-muted-foreground">{list.data?.counts[t.value] ?? 0}</span>
            </button>
          ))}
        </div>
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          Synced {timeAgo(list.data?.lastSyncedAt ?? null)}
          <Button type="button" variant="ghost" size="sm" onClick={() => runSync()} disabled={sync.isPending} aria-label="Sync with TikTok">
            <RefreshCw className={cn("h-3.5 w-3.5", sync.isPending && "animate-spin")} />
            Sync
          </Button>
        </span>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {tab === "TO_REVIEW" && items.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 border-b border-border bg-muted/40 px-4 py-2">
            <Checkbox
              checked={allSelected}
              disabled={selectable.length === 0}
              onCheckedChange={(v) => setSelected(v === true ? new Set(selectable.map((s) => s.id)) : new Set())}
              aria-label="Select all requests you can approve"
            />
            <span className="text-xs text-muted-foreground">{selected.size ? `${selected.size} selected` : "Select requests to review them together"}</span>
            {selected.size > 0 && (
              <span className="ml-auto flex gap-2">
                <Button type="button" size="sm" onClick={() => bulk("APPROVE")} disabled={busyIds.size > 0}>
                  <Check className="h-3.5 w-3.5" />
                  Approve {selected.size}
                </Button>
                <RejectMenu label={`Reject ${selected.size}`} disabled={busyIds.size > 0} onReject={(r) => bulk("REJECT", r)} />
              </span>
            )}
          </div>
        )}
        {list.isLoading ? (
          <SpinnerBlock className="py-16" />
        ) : list.error ? (
          <EmptyState bordered={false} title="Sample requests could not load" body={errorMessage(list.error)} action={<Button variant="outline" onClick={() => list.refetch()}>Try again</Button>} />
        ) : items.length === 0 ? (
          <EmptyState
            bordered={false}
            icon={<Gift />}
            title={tab === "TO_REVIEW" ? "No requests waiting" : `Nothing ${TABS.find((t) => t.value === tab)?.label.toLowerCase()} yet`}
            body={
              tab === "TO_REVIEW"
                ? "Creators request samples from your products on TikTok; they land here to review"
                : "Requests move here as TikTok updates them"
            }
          />
        ) : (
          items.map((s) => (
            <SampleRow
              key={s.id}
              s={s}
              selected={selected.has(s.id)}
              busy={busyIds.has(s.id)}
              onSelect={(checked) =>
                setSelected((prev) => {
                  const next = new Set(prev);
                  if (checked) next.add(s.id);
                  else next.delete(s.id);
                  return next;
                })
              }
              onOpen={() => setOpenId(s.id)}
              onApprove={() => review(s, "APPROVE")}
              onReject={(reason) => review(s, "REJECT", reason)}
            />
          ))
        )}
      </div>

      <SampleDetailSheet
        id={openId}
        onOpenChange={(open) => !open && setOpenId(null)}
        onReview={async (s, result, reason) => {
          await review(s, result, reason);
          queryClient.invalidateQueries({ queryKey: ["tiktok-sample", s.id] });
        }}
        onMessage={(s) => onMessage(pick(s))}
        onInvite={(s) => onInvite([pick(s)])}
      />
      <SamplePolicySheet open={policyOpen} onOpenChange={setPolicyOpen} />
      <SampleRulesSheet open={rulesOpen} onOpenChange={setRulesOpen} />
    </div>
  );
}
